import React, { useEffect, useMemo, useState } from 'react';
import { effectiveStage, highestQuote, soldValue, useFollowUps } from '../../services/followUps';
import { CALLS_LIVE, followUpTab, refreshCalls, togglePriority, useCalls } from '../../services/callsBoard';
import { avatarColours, initials } from './avatar';
import FollowUpDetail from './FollowUpDetail';
import CallDetail from './CallDetail';
import './Calls.css';

/**
 * Calls — every customer who needs a call, in the sections agreed 2026-10-21:
 *   Needs Quote · No Show · Follow Up (Due / Upcoming) · Booked · Dismissed, plus a ⭐ Priority filter.
 * Live: Zoho through services/callsBoard.js. Test mode: the test follow-ups.
 */

const SECTIONS = [
  { id: 'needsQuote', label: 'Needs Quote' },
  { id: 'noShow', label: 'No Show' },
  { id: 'followUp', label: 'Follow Up' },
  { id: 'booked', label: 'Booked' },
  { id: 'dismissed', label: 'Dismissed' },
];

const PERIODS = [
  { id: 30, label: '30d' },
  { id: 60, label: '60d' },
  { id: 90, label: '90d' },
  { id: 0, label: 'All' },
];

// links from the dashboard still use the old tab names
const FROM_OLD_TAB = { scheduled: ['followUp', 'upcoming'], call_now: ['followUp', 'due'], booked: ['booked', 'due'], dismissed: ['dismissed', 'due'] };

const ago = (iso) => {
  if (!iso) return '';
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  if (days < 0) return `in ${-days}d`;
  if (days === 0) return 'today';
  if (days < 14) return `${days}d ago`;
  if (days < 60) return `${Math.floor(days / 7)}w ago`;
  return `${Math.floor(days / 30)}mo ago`;
};
const day = (s) => new Date(s.length === 10 ? `${s}T12:00:00` : s).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
const money = (n) => `$${Math.round(Number(n) || 0).toLocaleString('en-CA')}`;

// test follow-ups → the same shape Zoho gives
const fromTestData = (f) => {
  const stage = effectiveStage(f);
  const section = stage === 'booked' ? 'booked' : stage === 'dismissed' ? 'dismissed' : 'followUp';
  return {
    id: f.id, kind: 'deal', section, name: f.name, address: f.address, phone: f.phone, priority: false,
    nextCall: f.callDate ? { dueDate: f.callDate.slice(0, 10) } : null, quotedAt: f.quotedAt, at: f.quotedAt,
    amount: f.booked ? soldValue(f) : highestQuote(f), lostReason: '',
  };
};

const chipFor = (item, tab) => {
  if (item.section === 'needsQuote') return { tone: 'call-now', icon: '📝', text: item.appointmentStart ? `Visited ${day(item.appointmentStart)}` : 'Needs quote' };
  if (item.section === 'noShow') return { tone: 'dismissed', icon: '↺', text: item.zohoStage === 'Cancelled' ? 'Cancelled' : 'Rebook' };
  if (item.section === 'booked') return { tone: 'booked', icon: '✓', text: 'Job booked' };
  if (item.section === 'dismissed') return { tone: 'dismissed', icon: '✕', text: item.lostReason ? `Lost · ${item.lostReason}` : 'Lost' };
  if (tab === 'upcoming') return { tone: 'scheduled', icon: '📅', text: `Call ${day(item.nextCall.dueDate)}` };
  return { tone: 'call-now', icon: '📞', text: item.nextCall ? `Call now · due ${day(item.nextCall.dueDate)}` : 'Call now · no call set' };
};

const Calls = ({ initialOpenId = null, initialTab = 'followUp', onOpenCommunication, onOpenInspection, onOpenRebook }) => {
  const testData = useFollowUps();
  const live = useCalls();
  const [startSection, startSub] = FROM_OLD_TAB[initialTab] || [initialTab, 'due'];
  const [section, setSection] = useState(SECTIONS.some((s) => s.id === startSection) ? startSection : 'followUp');
  const [sub, setSub] = useState(startSub);
  const [priorityOnly, setPriorityOnly] = useState(false);
  const [period, setPeriod] = useState(0);
  const [search, setSearch] = useState('');
  const [openId, setOpenId] = useState(initialOpenId);

  useEffect(() => {
    if (CALLS_LIVE) refreshCalls();
  }, []);

  const all = useMemo(() => (CALLS_LIVE ? live.items : testData.map(fromTestData)), [live.items, testData]);

  if (openId) {
    return CALLS_LIVE ? (
      <CallDetail
        id={openId}
        onBack={() => setOpenId(null)}
        onOpenCommunication={onOpenCommunication}
        onOpenInspection={onOpenInspection}
        onOpenRebook={onOpenRebook}
      />
    ) : (
      <FollowUpDetail id={openId} onBack={() => setOpenId(null)} onOpenCommunication={onOpenCommunication} />
    );
  }

  // search, period and the ⭐ filter narrow everything; the section (and sub-tab) picks the list
  const inView = all.filter((i) => {
    if (priorityOnly && !i.priority) return false;
    if (period && i.at && Date.now() - new Date(i.at).getTime() > period * 86400000) return false;
    const q = search.trim().toLowerCase();
    return !q || `${i.name} ${i.address} ${i.phone || ''}`.toLowerCase().includes(q);
  });
  const count = (id) => inView.filter((i) => i.section === id).length;
  const followUps = inView.filter((i) => i.section === 'followUp');
  const subCount = (t) => followUps.filter((i) => followUpTab(i) === t).length;

  const list = inView
    .filter((i) => i.section === section && (section !== 'followUp' || followUpTab(i) === sub))
    .sort((a, b) => {
      if (section === 'followUp') return ((a.nextCall && a.nextCall.dueDate) || '').localeCompare((b.nextCall && b.nextCall.dueDate) || '');
      return String(b.at || '').localeCompare(String(a.at || ''));
    });

  return (
    <div className="calls">
      <div className="calls-head">
        <div className="calls-title-row">
          <h1>Calls</h1>
          {CALLS_LIVE && (
            <button type="button" className="calls-refresh" onClick={() => refreshCalls()} disabled={live.load.status === 'loading'}>
              {live.load.status === 'loading' ? '⟳ Loading…' : '↻'}
            </button>
          )}
        </div>
        {CALLS_LIVE && live.load.status === 'error' && <p className="calls-error">⚠ {live.load.error}</p>}

        <div className="calls-search">
          <span className="calls-search-icon" aria-hidden="true">⌕</span>
          <input type="search" placeholder="Search by name, address or phone…" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>

        <div className="calls-periods">
          {PERIODS.map((p) => (
            <button key={p.id} type="button" className={`calls-period${period === p.id ? ' active' : ''}`} onClick={() => setPeriod(p.id)}>
              {p.label}
            </button>
          ))}
          <button
            type="button"
            className={`calls-period calls-priority-filter${priorityOnly ? ' active' : ''}`}
            onClick={() => setPriorityOnly((v) => !v)}
          >
            ⭐ Priority
          </button>
        </div>

        <div className="calls-tabs">
          {SECTIONS.map((s) => (
            <button key={s.id} type="button" className={`calls-tab${section === s.id ? ' active' : ''}`} onClick={() => setSection(s.id)}>
              {s.label}
              <span className="calls-tab-count">{count(s.id)}</span>
            </button>
          ))}
        </div>

        {section === 'followUp' && (
          <div className="calls-subtabs">
            {[
              ['due', 'Due'],
              ['upcoming', 'Upcoming'],
            ].map(([id, label]) => (
              <button key={id} type="button" className={`calls-subtab${sub === id ? ' active' : ''}`} onClick={() => setSub(id)}>
                {label} <span>{subCount(id)}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="calls-list">
        {CALLS_LIVE && live.load.status === 'loading' && !all.length && <p className="calls-empty">Loading from Zoho…</p>}
        {list.length === 0 && !(CALLS_LIVE && live.load.status === 'loading' && !all.length) && <p className="calls-empty">Nobody here right now.</p>}
        {list.map((i) => {
          const [bg, fg] = avatarColours(i.name);
          const chip = chipFor(i, section === 'followUp' ? followUpTab(i) : null);
          const showAmount = i.kind === 'deal' && i.amount > 0;
          return (
            <div className="calls-card-wrap" key={i.id}>
              <button type="button" className="calls-card" onClick={() => setOpenId(i.id)}>
                <span className="calls-avatar" style={{ background: bg, color: fg }}>{initials(i.name)}</span>
                <div className="calls-card-main">
                  <div className="calls-card-top">
                    <span className="calls-name">{i.name}</span>
                    <span className="calls-ago">{ago(i.kind === 'deal' ? i.quotedAt || i.at : i.at)}</span>
                  </div>
                  <div className="calls-address">{i.address}</div>
                  <div className="calls-chips">
                    <span className={`calls-chip ${chip.tone}`}>
                      <span aria-hidden="true">{chip.icon}</span> {chip.text}
                    </span>
                    {showAmount && (
                      <span className="calls-chip amount" title={i.section === 'booked' ? 'Sold' : 'Highest quote sent'}>
                        {money(i.amount)}
                      </span>
                    )}
                    {i.officeTasks && i.officeTasks.length > 0 && <span className="calls-chip scheduled">📋 Office task</span>}
                    {i.invoiceNotSent && <span className="calls-chip invoice-due">🧾 Invoice not sent</span>}
                  </div>
                </div>
              </button>
              {CALLS_LIVE && (
                <button
                  type="button"
                  className={`calls-star${i.priority ? ' on' : ''}`}
                  aria-label={i.priority ? 'Remove priority' : 'Mark priority'}
                  onClick={() => togglePriority(i).catch(() => refreshCalls())}
                >
                  {i.priority ? '★' : '☆'}
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Calls;
